import Logo from '@/assets/logo-primary.svg'

const OnboardingImageOne = 'https://commzone.nyc3.cdn.digitaloceanspaces.com/assets/onboarding-img-1.svg'
const OnboardingImageTwo = 'https://commzone.nyc3.cdn.digitaloceanspaces.com/assets/onboarding-img-2.svg'
import { useAuthContext } from '@/context'
import { useAvailableRoles } from '@/hooks/permissions/usePermissions'
import { useUpdateOrg } from '@/services/org/mutations'
import { useInviteUsers, useInviteUsersToBusiness } from '@/services/users/mutations'
import { useQueryBusinessMembers, useQueryUserRoles } from '@/services/users/queries'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type { OnboardingInvitee, OnboardingResponses, OnboardingStep } from '../types'
import { OnboardingComplete } from './OnboardingComplete'
import { OnboardingLoading } from './OnboardingLoading'
import { OnboardingSteps } from './OnboardingSteps'
import { defaultSteps } from './defaultSteps'

export type { OnboardingOption, OnboardingStep } from '../types'

interface OnboardingModalProps {
    isOpen: boolean
    userName?: string
    steps?: OnboardingStep[]
    onClose?: () => void
    onComplete?: (responses: OnboardingResponses) => void
    /** One-click dismiss — marks onboarding complete without walking every step */
    onSkipAll?: () => void
    skipAllLabel?: string
    loadingIllustrations?: ReactNode[]
    finalCtaLabel?: string
}

const INVITE_STEP_ID = 'invite-team'

const LOADING_ITEMS = [
    { id: 'workspace', label: 'Setting up your workspace' },
    { id: 'preferences', label: 'Customizing your preferences' },
    { id: 'finalizing', label: 'Finalizing CommZone setup' },
]

export const OnboardingModal = ({
    isOpen,
    steps = defaultSteps,
    onClose,
    onComplete,
    onSkipAll,
    skipAllLabel = 'Skip for now',
    loadingIllustrations: loadingIllustrationsProp,
    finalCtaLabel = 'Get started',
}: OnboardingModalProps) => {
    const { org, orgAccount, activeBusinessAccount } = useAuthContext()
    const totalSteps = steps.length
    const [currentStepIndex, setCurrentStepIndex] = useState(0)
    const [responses, setResponses] = useState<OnboardingResponses>({})
    const [otherInputs, setOtherInputs] = useState<Record<string, string>>({})
    const [inviteDraft, setInviteDraft] = useState('')
    const [phase, setPhase] = useState<'steps' | 'loading' | 'complete'>('steps')
    const [completedLoadingSteps, setCompletedLoadingSteps] = useState(0)
    const [activeLoadingIndex, setActiveLoadingIndex] = useState(0)
    const [currentIllustrationStage, setCurrentIllustrationStage] = useState(0)

    const hasCompletedRef = useRef(false)
    const { availableRoles: workspaceRoles } = useAvailableRoles({
        currentUserRole: orgAccount?.role?.name,
    })
    const { availableRoles: businessAssignableRoles } = useAvailableRoles({
        currentUserRole: activeBusinessAccount?.role?.name,
    })

    const { mutate: updateWorkspace, isLoading: isSavingWorkspace } = useUpdateOrg({
        onSuccess: () => {
            toast.success('Workspace name updated successfully!')
        },
        onError: () => {
            toast.error('Failed to update workspace name')
        },
    })

    const { mutateAsync: inviteUsersAsync, isLoading: isInvitingUsers } = useInviteUsers({
        onSuccess: () => {
            toast.success('Invitations sent successfully!')
        },
        onError: (error: any) => {
            const message = error?.response?.data?.message || 'Failed to send invitations. Please try again.'
            toast.error(message)
        },
    })

    const { mutateAsync: inviteUsersToBusinessAsync, isLoading: isInvitingToBusiness } = useInviteUsersToBusiness({
        onError: (error: any) => {
            const message = error?.response?.data?.message || 'Failed to invite teammates to the business.'
            toast.error(message)
        },
    })

    const { data: userRoles } = useQueryUserRoles()
    const { data: businessMembers } = useQueryBusinessMembers({})
    const businessMembersList = businessMembers?.data ?? []
    const businessMemberEmails = businessMembersList.map(member => member.email.toLowerCase())
    const businessUserRoleId = useMemo(() => {
        const rolePool = businessAssignableRoles.length ? businessAssignableRoles : (userRoles ?? [])
        const userRole = rolePool.find(role => role.name === 'user')
        return userRole?.id || rolePool[0]?.id || ''
    }, [businessAssignableRoles, userRoles])

    const illustrations = useMemo<ReactNode[]>(
        () =>
            loadingIllustrationsProp && loadingIllustrationsProp.length
                ? loadingIllustrationsProp
                : [
                      <img key='default-1' src={OnboardingImageOne} alt='CommZone onboarding in progress' />,
                      <img key='default-2' src={OnboardingImageTwo} alt='CommZone onboarding ready' />,
                  ],
        [loadingIllustrationsProp]
    )

    const currentStep = useMemo(() => steps[currentStepIndex], [steps, currentStepIndex])
    const isTextStep = currentStep.type === 'text'
    const isInviteStep = currentStep.type === 'invite'
    const isSingleSelect = currentStep.type === 'single-select'

    const currentResponse = responses[currentStep.id]
    const inviteResponse = responses[INVITE_STEP_ID]
    const inviteList = Array.isArray(inviteResponse) ? (inviteResponse as OnboardingInvitee[]) : []
    const selectedOptions = !isInviteStep && Array.isArray(currentResponse) ? (currentResponse as string[]) : []
    const textResponse = typeof currentResponse === 'string' ? currentResponse : ''
    const inviteesForStep = isInviteStep ? inviteList : []

    useEffect(() => {
        if (currentStep.id === 'workspace-name' && !responses['workspace-name'] && org?.name) {
            setResponses(prev => ({
                ...prev,
                ['workspace-name']: org.name,
            }))
        }
    }, [currentStep.id, org?.name, responses])

    useEffect(() => {
        if (!isInviteStep) {
            setInviteDraft('')
        }
    }, [currentStepIndex, isInviteStep])

    useEffect(() => {
        if (!isOpen) {
            setPhase('steps')
            setCurrentStepIndex(0)
            setResponses({})
            setInviteDraft('')
            setCompletedLoadingSteps(0)
            setActiveLoadingIndex(0)
            setCurrentIllustrationStage(0)
            hasCompletedRef.current = false
        }
    }, [isOpen])

    useEffect(() => {
        if (phase !== 'loading') return

        setCompletedLoadingSteps(0)
        setActiveLoadingIndex(0)
        setCurrentIllustrationStage(0)

        const stepDuration = 1400
        let index = 0
        let completionTimeout: ReturnType<typeof setTimeout> | null = null

        const interval = setInterval(() => {
            index += 1
            setCompletedLoadingSteps(Math.min(index, LOADING_ITEMS.length))
            setActiveLoadingIndex(index < LOADING_ITEMS.length ? index : LOADING_ITEMS.length)
            setCurrentIllustrationStage(prev => {
                const nextStage = illustrations.length ? Math.min(index, illustrations.length - 1) : 0
                return nextStage === prev ? prev : nextStage
            })

            if (index >= LOADING_ITEMS.length) {
                clearInterval(interval)
                completionTimeout = setTimeout(() => {
                    setPhase('complete')
                }, 800)
            }
        }, stepDuration)

        return () => {
            clearInterval(interval)
            if (completionTimeout) {
                clearTimeout(completionTimeout)
            }
        }
    }, [phase, illustrations.length])

    useEffect(() => {
        if (phase === 'complete' && !hasCompletedRef.current) {
            onComplete?.(responses)
            hasCompletedRef.current = true
        }
    }, [phase, onComplete, responses])

    const handleOptionToggle = (optionId: string) => {
        if (isTextStep || isInviteStep) return

        setResponses((prev: any) => {
            if (isSingleSelect) {
                return {
                    ...prev,
                    [currentStep.id]: [optionId],
                }
            } else {
                const previousSelections = prev[currentStep.id]
                const selectionsArray = Array.isArray(previousSelections) ? previousSelections : []
                const hasOption = selectionsArray.includes(optionId)
                const nextSelections = hasOption
                    ? selectionsArray.filter(id => id !== optionId)
                    : [...selectionsArray, optionId]

                return {
                    ...prev,
                    [currentStep.id]: nextSelections,
                }
            }
        })

        if (optionId === 'other-specify' && selectedOptions.includes('other-specify')) {
            setOtherInputs(prev => ({
                ...prev,
                [currentStep.id]: '',
            }))
        }
    }

    const handleOtherInputChange = (value: string) => {
        setOtherInputs(prev => ({
            ...prev,
            [currentStep.id]: value,
        }))
    }

    const handleWorkspaceSave = () => {
        const workspaceName = responses['workspace-name']
        if (typeof workspaceName === 'string' && workspaceName.trim() && orgAccount?.org?.id) {
            updateWorkspace({
                org: orgAccount.org.id,
                update: {
                    name: workspaceName.trim(),
                },
            })
        }
    }

    const handleTextChange = (value: string) => {
        if (!isTextStep) return
        setResponses(prev => ({
            ...prev,
            [currentStep.id]: value,
        }))
    }

    const handleInviteDraftChange = (value: string) => {
        if (!isInviteStep) return
        setInviteDraft(value)
    }

    const handleAddInvite = () => {
        if (!isInviteStep) return
        const trimmed = inviteDraft.trim()
        if (!trimmed) return

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailPattern.test(trimmed)) {
            toast.error('Please enter a valid email address.')
            return
        }

        setResponses(prev => {
            const existing = prev[currentStep.id]
            const list = Array.isArray(existing) ? (existing as OnboardingInvitee[]) : []
            if (list.some(invite => invite.email.toLowerCase() === trimmed.toLowerCase())) {
                toast.error('This email is already on your invite list.')
                return prev
            }
            return {
                ...prev,
                [currentStep.id]: [...list, { email: trimmed, role: '' }],
            }
        })
        setInviteDraft('')
    }

    const handleRemoveInvite = (email: string) => {
        if (!isInviteStep) return
        setResponses(prev => {
            const existing = prev[currentStep.id]
            const list = Array.isArray(existing) ? (existing as OnboardingInvitee[]) : []
            return {
                ...prev,
                [currentStep.id]: list.filter(item => item.email.toLowerCase() !== email.toLowerCase()),
            }
        })
    }

    const handleInviteRoleChange = (email: string, roleId: string) => {
        setResponses(prev => {
            const existing = prev[INVITE_STEP_ID]
            const list = Array.isArray(existing) ? (existing as OnboardingInvitee[]) : []
            const nextList = list.map(invite =>
                invite.email.toLowerCase() === email.toLowerCase() ? { ...invite, role: roleId } : invite
            )
            return {
                ...prev,
                [INVITE_STEP_ID]: nextList,
            }
        })
    }

    const handlePrevious = () => {
        if (phase !== 'steps') return
        setCurrentStepIndex(prev => Math.max(0, prev - 1))
    }

    const handleNext = () => {
        if (phase !== 'steps') return
        if (currentStepIndex >= totalSteps - 1) {
            handleFinish()
            return
        }
        setCurrentStepIndex(prev => Math.min(totalSteps - 1, prev + 1))
    }

    const handleSkip = () => {
        if (phase !== 'steps') return
        if (currentStepIndex === totalSteps - 1) {
            handleFinish()
        } else {
            setCurrentStepIndex(prev => Math.min(totalSteps - 1, prev + 1))
        }
    }

    const startLoadingPhase = () => {
        setPhase('loading')
        setCompletedLoadingSteps(0)
        setActiveLoadingIndex(0)
        setCurrentIllustrationStage(0)
    }

    const handleFinish = async () => {
        if (phase !== 'steps' || isInvitingUsers || isInvitingToBusiness) return

        const invites = Array.isArray(responses[INVITE_STEP_ID])
            ? (responses[INVITE_STEP_ID] as OnboardingInvitee[])
            : []
        const hasMissingRole = invites.some(invite => !invite.role)

        if (hasMissingRole) {
            toast.error('Please select a role for each invitee before completing onboarding.')
            return
        }

        if (invites.length && !orgAccount?.org?.id) {
            toast.error('Workspace information is missing. Please try again later.')
            return
        }

        if (invites.length && orgAccount?.org?.id) {
            const pendingBusinessInvites = invites.filter(
                invite => !businessMemberEmails.includes(invite.email.toLowerCase())
            )
            const workspaceInvites = invites.filter(invite => businessMemberEmails.includes(invite.email.toLowerCase()))

            if (pendingBusinessInvites.length) {
                if (!businessUserRoleId) {
                    toast.error('No assignable business role found. Please contact an administrator.')
                    return
                }

                try {
                    await Promise.all(
                        pendingBusinessInvites.map(invite =>
                            inviteUsersToBusinessAsync({
                                org: orgAccount.org.id,
                                invite: {
                                    email: invite.email,
                                    role: businessUserRoleId,
                                    workspaces: [
                                        {
                                            workspace: orgAccount.org.id,
                                            role: invite.role as string,
                                        },
                                    ],
                                },
                            })
                        )
                    )
                    toast.success('Invited new teammates to your business.')
                } catch {
                    return
                }
            }

            if (workspaceInvites.length) {
                try {
                    await inviteUsersAsync({
                        org: orgAccount.org.id,
                        invites: workspaceInvites.map(invite => ({
                            email: invite.email,
                            role: invite.role,
                        })),
                    })
                } catch {
                    return
                }
            }
        }

        startLoadingPhase()
    }

    const handleGetStarted = () => {
        onClose?.()
    }

    if (!isOpen) {
        return null
    }

    const primaryActionLabel =
        currentStep.primaryActionLabel || (currentStepIndex === totalSteps - 1 ? 'Finish' : 'Next')

    const progress = ((currentStepIndex + 1) / totalSteps) * 100
    const showPrevious = currentStepIndex > 0
    const isLastStep = currentStepIndex === totalSteps - 1

    const primaryActionLoading = isLastStep ? isInvitingUsers || isInvitingToBusiness : false

    const bodyContent =
        phase === 'steps' ? (
            <OnboardingSteps
                step={currentStep}
                stepIndex={currentStepIndex}
                totalSteps={totalSteps}
                selectedOptions={selectedOptions}
                textResponse={textResponse}
                inviteDraft={inviteDraft}
                invitees={inviteesForStep}
                progress={progress}
                primaryActionLabel={primaryActionLabel}
                showPrevious={showPrevious}
                isTextStep={isTextStep}
                isInviteStep={isInviteStep}
                onOptionToggle={handleOptionToggle}
                onTextChange={handleTextChange}
                onInviteDraftChange={handleInviteDraftChange}
                onAddInvite={handleAddInvite}
                onRemoveInvite={handleRemoveInvite}
                onInviteRoleChange={handleInviteRoleChange}
                onPrevious={handlePrevious}
                onPrimaryAction={isLastStep ? handleFinish : handleNext}
                onWorkspaceSave={handleWorkspaceSave}
                isSavingWorkspace={isSavingWorkspace}
                isPrimaryActionLoading={primaryActionLoading}
                otherInputValue={otherInputs[currentStep.id]}
                onOtherInputChange={handleOtherInputChange}
                workspaceRoles={workspaceRoles}
            />
        ) : phase === 'loading' ? (
            <OnboardingLoading
                logoSrc={Logo}
                illustrations={illustrations}
                loadingItems={LOADING_ITEMS}
                completedLoadingSteps={completedLoadingSteps}
                activeLoadingIndex={activeLoadingIndex}
                currentIllustrationStage={currentIllustrationStage}
            />
        ) : (
            <OnboardingComplete logoSrc={Logo} finalCtaLabel={finalCtaLabel} onGetStarted={handleGetStarted} />
        )

    const headerRightContent =
        phase === 'steps' ? (
            onSkipAll ? (
                <button
                    type='button'
                    onClick={onSkipAll}
                    className='text-sm font-semibold text-white/90 underline-offset-2 hover:text-white hover:underline'
                >
                    {skipAllLabel}
                </button>
            ) : !isLastStep ? (
                <span onClick={handleSkip} className='text-sm font-medium cursor-pointer text-onboarding-status-skip'>
                    Skip
                </span>
            ) : null
        ) : null

    return (
        <div className='fixed inset-0 z-[1000] flex items-center justify-center bg-onboarding-overlay/40 px-4 py-10 backdrop-blur-sm'>
            <div
                className='relative w-full max-w-3xl rounded-[28px] bg-white shadow-[0_40px_80px_rgba(48,21,64,0.18)]'
                role='dialog'
                aria-modal='true'
            >
                <div className='flex flex-col'>
                    <div className='flex items-center justify-between rounded-t-[28px] bg-gradient-to-b from-onboarding-header-start to-onboarding-header-end px-10 py-6'>
                        <img src={Logo} alt='CommZone logo' className='w-auto h-10' />
                        {headerRightContent}
                    </div>
                    {bodyContent}
                </div>
            </div>
        </div>
    )
}
